// src/components/Chat/MessageList.tsx
// Daftar pesan chat — clean, readable

import React, { useEffect, useRef } from "react";
import { ChatMessage } from "../../types/chatTypes";

function renderMarkdown(text: string): React.ReactNode[] {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  lines.forEach((line, lineIdx) => {
    if (lineIdx > 0) elements.push(<br key={`br-${lineIdx}`} />);
    const parts = line.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    parts.forEach((part, partIdx) => {
      const key = `${lineIdx}-${partIdx}`;
      if (part.startsWith("**") && part.endsWith("**")) {
        elements.push(<strong key={key}>{part.slice(2, -2)}</strong>);
      } else if (part.startsWith("*") && part.endsWith("*")) {
        elements.push(<em key={key}>{part.slice(1, -1)}</em>);
      } else {
        elements.push(<span key={key}>{part}</span>);
      }
    });
  });
  return elements;
}

function formatTime(date: Date): string {
  return new Date(date).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
}

const MessageList: React.FC<{ messages: ChatMessage[] }> = ({ messages }) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <div style={{
      flex: 1, overflowY: "auto", padding: "12px 10px",
      display: "flex", flexDirection: "column", gap: 10,
      background: "#1e2139",
    }}>
      <style>{`
        @keyframes typingBounce {
          0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      {messages.map((msg) => {
        const isUser = msg.role === "user";
        return (
          <div key={msg.id} style={{
            display: "flex", alignItems: "flex-end", gap: 6,
            flexDirection: isUser ? "row-reverse" : "row",
          }}>
            {!isUser && (
              <div style={{
                width: 24, height: 24, borderRadius: "50%",
                background: "linear-gradient(135deg, #4a90e2 0%, #357abd 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
            )}
            <div style={{
              maxWidth: "80%", padding: "8px 12px", borderRadius: 14,
              fontSize: 12.5, lineHeight: 1.5, wordWrap: "break-word",
              whiteSpace: "pre-wrap",
              ...(isUser
                ? { background: "linear-gradient(135deg, #4a90e2 0%, #357abd 100%)", color: "white", borderBottomRightRadius: 4 }
                : { background: "#2a2f48", color: "#e0e4f0", borderBottomLeftRadius: 4 }),
            }}>
              {msg.isLoading ? (
                <div style={{ display: "flex", gap: 4, padding: "2px 0" }}>
                  {[0, 1, 2].map((i) => (
                    <span key={i} style={{
                      width: 6, height: 6, borderRadius: "50%", background: "#7a8099",
                      display: "inline-block",
                      animation: `typingBounce 1.4s infinite ease-in-out ${i * 0.16}s`,
                    }} />
                  ))}
                </div>
              ) : (
                <>
                  <div>{renderMarkdown(msg.content)}</div>
                  <div style={{ fontSize: 9, opacity: 0.5, marginTop: 3, textAlign: "right" }}>
                    {formatTime(msg.timestamp)}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
};

export default MessageList;
